import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Chip,
  TextField,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
} from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import AsyncFieldWrapper from '../AsyncFieldWrapper';

// MUI v4 useAutocomplete resets `inputValue` whenever the `value` prop's
// reference changes (useAutocomplete.js:227-229), wiping typed text in
// multi-select when consumers pass a fresh array each render. Stabilize by
// deep-equality so reference only changes on real content change.
function useDeepMemo<T>(value: T): T {
  const ref = useRef(value);
  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

type OmittedMuiProps =
  | 'value'
  | 'onChange'
  | 'options'
  | 'getOptionLabel'
  | 'getOptionSelected'
  | 'loading'
  | 'onOpen'
  | 'onClose'
  | 'onInputChange'
  | 'disabled'
  | 'fullWidth'
  | 'className'
  | 'multiple'
  | 'renderInput';

interface BaseAutocompleteProps<T> {
  label: string;
  getOptionLabel: (option: T) => string;
  getOptionValue?: (option: T) => string;
  debounceMs?: number;
  disabled?: boolean;
  error?: string;
  fullWidth?: boolean;
  className?: string;
  'data-cy'?: string;
  loadTrigger?: 'focus' | 'mount';
  variant?: 'filled' | 'standard' | 'outlined';
  helperText?: string;
  loading?: boolean;
  options?: T[];
  loadOptions?: (searchTerm: string) => Promise<T[]>;
  clientSideFilter?: boolean;
  /** Options the user may see but not pick. */
  getOptionDisabled?: (option: T) => boolean;
  /** Selected options the user may not remove (chip without the delete icon). */
  isOptionLocked?: (option: T) => boolean;
}

interface SingleAutocompleteProps<T>
  extends BaseAutocompleteProps<T>,
    Omit<MuiAutocompleteProps<T, false, false, false>, OmittedMuiProps> {
  multiple?: false;
  value: T | null;
  onChange: (value: T | null) => void;
}

interface MultipleAutocompleteProps<T>
  extends BaseAutocompleteProps<T>,
    Omit<MuiAutocompleteProps<T, true, false, false>, OmittedMuiProps> {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
}

export type AutocompleteProps<T> =
  | SingleAutocompleteProps<T>
  | MultipleAutocompleteProps<T>;

export function Autocomplete<T>(props: AutocompleteProps<T>) {
  const {
    label,
    value,
    onChange,
    getOptionLabel,
    getOptionValue = (option: any) => option?.id ?? '',
    debounceMs = 300,
    disabled = false,
    error,
    fullWidth = false,
    className,
    'data-cy': dataCy,
    loadTrigger = 'focus',
    variant = 'filled',
    helperText,
    loading: externalLoading,
    multiple,
    options: _staticOptions,
    loadOptions: _loadOptions,
    clientSideFilter = false,
    getOptionDisabled,
    isOptionLocked,
    ...muiProps
  } = props;

  const classes = useStyles();
  const isStaticMode = 'options' in props && props.options !== undefined;
  const loadOptions = 'loadOptions' in props ? props.loadOptions : undefined;
  const staticOptions = 'options' in props ? props.options : undefined;

  const [internalOptions, setInternalOptions] = useState<T[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(
    !isStaticMode && loadTrigger === 'mount',
  );
  const requestIdRef = useRef(0);

  const currentOptions = isStaticMode ? staticOptions ?? [] : internalOptions;
  const isLoading = externalLoading || internalLoading;

  const stableValue = useDeepMemo(value);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (!loadOptions) return;

        requestIdRef.current += 1;
        const requestId = requestIdRef.current;

        setInternalLoading(true);
        try {
          const results = await loadOptions(searchTerm);
          if (requestId === requestIdRef.current) {
            setInternalOptions(results);
          }
        } catch (err) {
          if (requestId === requestIdRef.current) {
            console.error('[Autocomplete] Error loading options:', err);
            setInternalOptions([]);
          }
        } finally {
          if (requestId === requestIdRef.current) {
            setInternalLoading(false);
          }
        }
      }, debounceMs),
    [loadOptions, debounceMs],
  );

  const fetchImmediately = useCallback(
    (term: string) => {
      debouncedFetch.cancel();
      debouncedFetch(term);
      debouncedFetch.flush();
    },
    [debouncedFetch],
  );

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  useEffect(() => {
    if (!isStaticMode && loadTrigger === 'mount' && loadOptions) {
      const fetchInitialOptions = async () => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;

        setInternalLoading(true);
        try {
          const results = await loadOptions('');
          if (requestId === requestIdRef.current) {
            setInternalOptions(results);
          }
        } catch {
          if (requestId === requestIdRef.current) {
            setInternalOptions([]);
          }
        } finally {
          if (requestId === requestIdRef.current) {
            setInternalLoading(false);
            setIsInitialLoad(false);
          }
        }
      };

      fetchInitialOptions();
    }
  }, [loadTrigger, loadOptions, isStaticMode]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (
      !isStaticMode &&
      loadTrigger === 'focus' &&
      internalOptions.length === 0
    ) {
      fetchImmediately('');
    }
  }, [fetchImmediately, loadTrigger, internalOptions.length, isStaticMode]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    debouncedFetch.cancel();
  }, [debouncedFetch]);

  const handleInputChange = useCallback(
    (
      _event: React.ChangeEvent<object>,
      newInputValue: string,
      reason: string,
    ) => {
      if (isStaticMode || clientSideFilter) return;

      if (reason === 'clear') {
        fetchImmediately('');
        return;
      }
      if (reason === 'input' && isOpen) {
        debouncedFetch(newInputValue);
      }
    },
    [debouncedFetch, fetchImmediately, isOpen, isStaticMode, clientSideFilter],
  );

  const handleChange = useCallback(
    (_event: React.ChangeEvent<object>, newValue: T | T[] | null) => {
      if (multiple && isOptionLocked && Array.isArray(stableValue)) {
        // Locked selections cannot be removed: put them back if MUI dropped them.
        const next = (newValue as T[] | null) ?? [];
        const kept = (stableValue as T[]).filter(
          current =>
            isOptionLocked(current) &&
            !next.some(n => getOptionValue(n) === getOptionValue(current)),
        );
        (onChange as (value: T[]) => void)([...kept, ...next]);
        return;
      }
      (onChange as (value: T | T[] | null) => void)(newValue);
    },
    [onChange, multiple, isOptionLocked, stableValue, getOptionValue],
  );

  const showWrapperLoader = isStaticMode
    ? externalLoading
    : externalLoading || (internalLoading && isInitialLoad);

  return (
    <AsyncFieldWrapper loading={!!showWrapperLoader}>
      <MuiAutocomplete
        {...muiProps}
        multiple={multiple}
        className={`${classes.root} ${className || ''}`}
        options={currentOptions}
        getOptionLabel={getOptionLabel}
        getOptionSelected={(option, val) =>
          getOptionValue(option) === getOptionValue(val)
        }
        getOptionDisabled={getOptionDisabled}
        renderTags={
          multiple && isOptionLocked
            ? (tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const tagProps = getTagProps({ index }) as Record<
                    string,
                    unknown
                  >;
                  const locked = isOptionLocked(option);
                  return (
                    <Chip
                      {...tagProps}
                      key={getOptionValue(option)}
                      label={getOptionLabel(option)}
                      onDelete={
                        locked ? undefined : (tagProps.onDelete as () => void)
                      }
                    />
                  );
                })
            : undefined
        }
        openOnFocus
        loading={isLoading}
        value={stableValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onOpen={handleOpen}
        onClose={handleClose}
        disabled={disabled}
        fullWidth={fullWidth}
        data-cy={dataCy}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            variant={variant}
            margin="dense"
            error={!!error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && !isInitialLoad && !isStaticMode && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && !helperText && (
        <FormHelperText className={classes.errorText} error>
          {error}
        </FormHelperText>
      )}
    </AsyncFieldWrapper>
  );
}

const useStyles = makeStyles({
  root: {
    marginBottom: '8px',
    minWidth: '220px',
  },
  errorText: {
    marginLeft: 14,
    marginTop: 0,
  },
});

export default Autocomplete;
